import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import TemplateDataTable from '../shared/TemplateDataTable';

export default function ReportingFailuresTable({
  rows = [],
  isLoading,
  showAffectedTemplates = true,
  emptyMessage = 'No failed messages in this date range.',
}) {
  const columns = useMemo(() => {
    const base = [
      {
        accessorKey: 'failure_code',
        header: 'Failure Code',
        enableSorting: false,
        Cell: ({ row }) => {
          const failureCode = row.original?.failure_code;
          const failureReason = row.original?.failure_reason;

          // Use failure_code if available,
          // otherwise get code from failure_reason[0]
          if (failureCode !== null && failureCode !== undefined) {
            return String(failureCode);
          }

          if (Array.isArray(failureReason) && failureReason.length > 0) {
            return failureReason[0]?.code ?? '-';
          }

          return '-';
        },
      },
      {
        accessorKey: 'failure_reason',
        header: 'Reason',
        enableSorting: false,
        Cell: ({ cell }) => {
          const value = cell.getValue();

          if (!value) {
            return '-';
          }

          // failure_reason is an array of objects
          if (Array.isArray(value)) {
            return value
              .map(
                (item) =>
                  item?.message ||
                  item?.title ||
                  item?.code ||
                  '-'
              )
              .filter(Boolean)
              .join(', ');
          }

          // Handle single object
          if (typeof value === 'object') {
            return (
              value?.message ||
              value?.title ||
              value?.code ||
              '-'
            );
          }

          return String(value);
        },
      },
      {
        accessorKey: 'count',
        header: 'Count',
        enableSorting: true,
      },
    ];

    if (showAffectedTemplates) {
      base.push({
        id: 'affected_templates',
        header: 'Affected Templates',
        accessorFn: (row) => (row.affected_templates ?? []).length,
        enableSorting: true,
      });
    }

    return base;
  }, [showAffectedTemplates]);

  if (isLoading) {
    return (
      <Typography variant="body2">
        Loading failure breakdown…
      </Typography>
    );
  }

  if (!rows.length) {
    return (
      <Box sx={{ py: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <TemplateDataTable
      columns={columns}
      data={rows}
      enableDensityToggle={false}
      enableFullScreenToggle={false}
      enableColumnActions={false}
      enableColumnFilters={false}
      enableHiding={false}
    />
  );
}